import React, { useRef, useCallback } from 'react'
import { Overlay, OverlayProps } from '../components/Overlay/index.js'
import { createRoot, Root } from 'react-dom/client'

const activeOverlays = new WeakMap<HTMLElement, Root>()

function isOverlayComponent(component: any): boolean {
  return (
    component &&
    (component._isOverlayComponent ||
      component.name === 'Dialog' ||
      component.name === 'Modal' ||
      (component.prototype &&
        typeof component.prototype.render === 'function' &&
        'open' in (component.defaultProps || {})))
  )
}

export default function useOverlay<T>(
  Component: React.ComponentType<T>,
  defaultProps?: T,
  overlayProps?: Omit<OverlayProps, 'open' | 'children'>,
): (element?: any) => void {
  const cleanupRef = useRef<(() => void) | null>(null)

  return useCallback(
    (element) => {
      if (typeof document === 'undefined') {
        console.warn('useOverlay: document is not available')
        return
      }

      try {
        const container = document.createElement('div')
        const root = createRoot(container)

        activeOverlays.set(container, root)

        const triggerElement = element?.target || null

        // Render the overlay
        root.render(
          isOverlayComponent(Component) ? (
            <Component {...defaultProps} open />
          ) : (
            <Overlay open {...overlayProps} triggerEl={triggerElement}>
              <Overlay.Content>
                <Component {...defaultProps} />
              </Overlay.Content>
            </Overlay>
          ),
        )
      } catch (error) {
        console.error('Error creating overlay:', error)
        if (cleanupRef.current) {
          cleanupRef.current()
        }
      }
    },
    [Component, defaultProps, overlayProps],
  )
}

export { useOverlay }
